/**
 * Liest die Textebene eines PDF im Browser (ADR 0018) — der **einzige** Ort,
 * an dem `pdfjs-dist` steht (Spec 09, „Was neu entsteht"). Das Ergebnis geht
 * unverändert in denselben `POST /documents`, der auch die Datei
 * registriert (`useDocumentUpload.ts`) — nicht in einem zweiten Aufruf.
 *
 * **Koordinaten (AK-570):** Jeder Kasten liegt in `0..1`, Ursprung oben
 * links. `page.getViewport({ scale: 1 })` liefert eine `PageViewport`, deren
 * `convertToViewportPoint` die Drehung der Seite **und** den Wechsel von
 * PDFs Ursprung unten links auf den hier verlangten Ursprung oben links
 * bereits erledigt — reine Bibliotheksfunktion, keine eigene Trigonometrie,
 * kein DPI- und kein Punktwert an dieser Stelle. Die anschließende Division
 * durch `viewport.width`/`.height` ist Normierung auf `0..1`, keine
 * Einheitenumrechnung.
 *
 * **Scheitert das Lesen** (kaputtes PDF, keine Textebene, Ausnahme aus
 * `pdfjs-dist`), liefert diese Datei `text_source: 'none'` — die Datei wird
 * trotzdem abgelegt (Spec 09, „Während der Text gelesen wird": „Ein
 * Dokument, das nicht gespeichert wird, weil sein Text nicht lesbar war,
 * wäre die falsche Reihenfolge").
 */

// Nur ein einziger, technisch notwendiger MIME-Typ-Literal: welche der fünf
// erlaubten Dateiarten überhaupt eine Textebene *haben kann* — nicht die
// Positivliste selbst (`DOCUMENT_ALLOWED_MIME_TYPES`, AK-500 verbietet
// genau die als Literal). Ein Bild hat nie eine Textebene, unabhängig davon,
// welche Bildtypen die Konfiguration gerade erlaubt.
const PDF_MIME_TYPE = 'application/pdf'

export interface DocumentTextBlock {
  readonly page: number
  readonly text: string
  readonly box: readonly [number, number, number, number]
}

export interface DocumentTextResult {
  readonly textSource: 'pdf_layer' | 'none'
  readonly text: string | null
  readonly pages: number | null
  readonly blocks: DocumentTextBlock[] | null
}

export const NO_TEXT_RESULT: DocumentTextResult = { textSource: 'none', text: null, pages: null, blocks: null }

/** Ein einzelnes Textelement aus `pdfjs-dist`s `getTextContent()` — nur die
 * Felder, die diese Datei benutzt (nicht der volle `TextItem`-Typ der
 * Bibliothek, damit die Kernrechnung ohne `pdfjs-dist` selbst testbar ist,
 * siehe `pdfText.test.ts`). */
export interface RawTextItem {
  readonly str: string
  readonly transform: readonly number[]
  readonly width: number
  readonly hasEOL: boolean
}

/** Wandelt Bibliotheks-Punkte in `[x, y]` — die Form, die
 * `convertToViewportPoint` liefert. */
export type ViewportPoint = (x: number, y: number) => readonly [number, number]

/**
 * Ein Textelement in einen normierten Block — reine Funktion, kein
 * `pdfjs-dist`-Import. `toViewportPoint` kapselt `page.getViewport(...).
 * convertToViewportPoint`, `pageWidth`/`pageHeight` sind
 * `viewport.width`/`.height`.
 *
 * Liefert `null` bei einem leeren Textelement oder einem entarteten Kasten
 * (z. B. Rundungsfehler auf Breite/Höhe 0) — ein solcher Block wäre nach der
 * Serverprüfung ohnehin 422 (`x0 < x1`, `y0 < y1`).
 */
export function buildTextBlock(
  item: RawTextItem,
  page: number,
  toViewportPoint: ViewportPoint,
  pageWidth: number,
  pageHeight: number,
): DocumentTextBlock | null {
  const text = item.str.trim()
  if (text === '') return null
  if (pageWidth <= 0 || pageHeight <= 0) return null

  // `transform[2]`/`[3]` sind die vertikale Skalierung des Textlaufs — ihre
  // Länge ist die Schrifthöhe in derselben Einheit wie die Übersetzung
  // (`transform[4]`/`[5]`), dieselbe Rechnung, die `pdfjs-dist` selbst für
  // seine Textebene benutzt (kein DPI-, kein Punktwert).
  const fontHeight = Math.hypot(item.transform[2] ?? 0, item.transform[3] ?? 0)
  const x0Pdf = item.transform[4] ?? 0
  const y0Pdf = item.transform[5] ?? 0
  const x1Pdf = x0Pdf + item.width
  const y1Pdf = y0Pdf + fontHeight

  const [vx0, vy0] = toViewportPoint(x0Pdf, y0Pdf)
  const [vx1, vy1] = toViewportPoint(x1Pdf, y1Pdf)

  const left = Math.min(vx0, vx1) / pageWidth
  const right = Math.max(vx0, vx1) / pageWidth
  const top = Math.min(vy0, vy1) / pageHeight
  const bottom = Math.max(vy0, vy1) / pageHeight

  const box: readonly [number, number, number, number] = [
    clamp01(left),
    clamp01(top),
    clamp01(right),
    clamp01(bottom),
  ]
  if (box[0] >= box[2] || box[1] >= box[3]) return null

  return { page, text, box }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(1, Math.max(0, value))
}

/** Fügt die Textelemente einer Seite zu Zeilentext zusammen — `hasEOL`
 * markiert bereits das Zeilenende, dieselbe Regel, die `pdfjs-dist`s eigener
 * Textebenen-Baustein benutzt. */
export function joinPageText(items: readonly RawTextItem[]): string {
  let joined = ''
  for (const item of items) {
    joined += item.str
    joined += item.hasEOL ? '\n' : ''
  }
  return joined
}

/**
 * Liest die Textebene eines PDF. Nicht-PDF-Dateien bekommen sofort
 * `text_source: 'none'` — der Browser hat keine Erkennung für Bilder
 * (ADR 0018).
 */
export async function readDocumentText(file: File): Promise<DocumentTextResult> {
  if (file.type !== PDF_MIME_TYPE) return NO_TEXT_RESULT

  try {
    const pdfjs = await import('pdfjs-dist')
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

    const buffer = await file.arrayBuffer()
    const doc = await pdfjs.getDocument({ data: buffer }).promise

    let text = ''
    const blocks: DocumentTextBlock[] = []

    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
      const page = await doc.getPage(pageNumber)
      const viewport = page.getViewport({ scale: 1 })
      const content = await page.getTextContent()

      // `TextMarkedContent` (kein `str`) fällt heraus — dieselbe
      // Unterscheidung, die `pdfjs-dist`s eigener Text-Layer trifft. `in`
      // engt die Vereinigung `TextItem | TextMarkedContent` direkt ein
      // (TypeScripts eingebaute Narrowing-Regel), ohne `TextItem` selbst zu
      // importieren — der Typ ist am Modulwurzel von `pdfjs-dist` nicht
      // exportiert, nur über einen internen Unterpfad erreichbar.
      const items: RawTextItem[] = content.items.flatMap((item) =>
        'str' in item ? [{ str: item.str, transform: item.transform, width: item.width, hasEOL: item.hasEOL }] : [],
      )

      text += joinPageText(items)
      text += '\n'

      for (const item of items) {
        const block = buildTextBlock(
          item,
          pageNumber,
          (x, y) => viewport.convertToViewportPoint(x, y) as [number, number],
          viewport.width,
          viewport.height,
        )
        if (block !== null) blocks.push(block)
      }
    }

    const trimmed = text.trim()
    if (trimmed === '') return NO_TEXT_RESULT

    return { textSource: 'pdf_layer', text: trimmed, pages: doc.numPages, blocks }
  } catch {
    // Kaputtes PDF, nicht unterstütztes Feature, o. ä. — abgelegt wird
    // trotzdem, nur ohne Text (Spec 09, „Während der Text gelesen wird").
    return NO_TEXT_RESULT
  }
}
